import { Box, FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, type DixaTypographySize } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ampli } from 'lib-shared-tracking';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

import {
  DashboardLegend,
  DashboardLegendNumberItem,
} from 'app/views/layout/views/dashboard/components/legend';
import { createSearchUrlParams } from 'app/views/layout/views/search/utils';
import { AppLink } from 'components/appLink';

import { useChannelDashboardChannelContext } from '../../../../channelDashboardChannel.context';
import { ChannelDashboardMetric } from '../../../metric/channelDashboardMetric';

import styles from './channelPendingConversationsMetric.module.scss';

const DIAMETER = 56;
const STROKE = 6;

type ChannelDashboardPendingConversationsMetricProps = PropsWithClassName;

export const ChannelDashboardPendingConversationsMetric = memo(
  function ChannelDashboardPendingConversationsMetric({
    className,
  }: ChannelDashboardPendingConversationsMetricProps): JSX.Element {
    const { metrics, channels, isSkeleton } =
      useChannelDashboardChannelContext();

    const items = useMemo(
      (): readonly DashboardLegendNumberItem[] => [
        {
          type: 'number',
          title: 'Waiting',
          format: 'number',
          value: metrics?.conversations_in_queue ?? 0,
          color: colors.orange[500],
          linkProps: {
            path: '/search/conversations',
            params: createSearchUrlParams({
              filters: {
                strategy: 'all',
                filters: [
                  {
                    field: 'initial_channel',
                    operator: 'is_one',
                    values: channels.map((channel) => ({
                      value: convertChannelCasing(channel, 'truncate'),
                    })),
                  },
                  {
                    field: 'status',
                    operator: 'is_one',
                    values: [{ value: 'open' }],
                  },
                  {
                    field: 'agent_id',
                    operator: 'is_empty',
                    values: [],
                  },
                  {
                    field: 'queue_id',
                    operator: 'is_not_empty',
                    values: [],
                  },
                ],
              },
            }),
            onClick: () => {
              ampli.dashboardChannelPendingConversationsMetricClicked({
                metric: 'in_queue',
              });
            },
          },
        },
        {
          type: 'number',
          title: 'Assigned',
          format: 'number',
          value: metrics?.conversations_open ?? 0,
          color: colors.purple[400],
          linkProps: {
            path: '/search/conversations',
            params: createSearchUrlParams({
              filters: {
                strategy: 'all',
                filters: [
                  {
                    field: 'initial_channel',
                    operator: 'is_one',
                    values: channels.map((channel) => ({
                      value: convertChannelCasing(channel, 'truncate'),
                    })),
                  },
                  {
                    field: 'status',
                    operator: 'is_one',
                    values: [{ value: 'open' }],
                  },
                  {
                    field: 'agent_id',
                    operator: 'is_not_empty',
                    values: [],
                  },
                  {
                    field: 'queue_id',
                    operator: 'is_not_empty',
                    values: [],
                  },
                ],
              },
            }),
            onClick: () => {
              ampli.dashboardChannelPendingConversationsMetricClicked({
                metric: 'open',
              });
            },
          },
        },
      ],
      [channels, metrics?.conversations_in_queue, metrics?.conversations_open],
    );

    const chartData = useMemo(
      () => items.filter(({ value }) => !isSkeleton && !!value),
      [isSkeleton, items],
    );

    const total = useMemo(
      (): number => chartData.reduce((acc, { value }) => acc + value, 0),
      [chartData],
    );

    const totalFontSize = useMemo((): DixaTypographySize => {
      if (total >= 100000) return '200';
      if (total >= 10000) return '300';
      if (total >= 1000) return '400';
      return '500';
    }, [total]);

    return (
      <ChannelDashboardMetric
        className={className}
        title="Pending conversations"
        isSkeleton={isSkeleton}
        metricType="pending_conversations"
      >
        <FlexBox gap="250" alignItems="center">
          {isSkeleton ? (
            <Skeleton
              className={styles.skeleton}
              width={`${DIAMETER}px`}
              height={`${DIAMETER}px`}
              borderRadius="50%"
              flex="0 0 auto"
            >
              <Box
                width={`${DIAMETER - STROKE * 2}px`}
                height={`${DIAMETER - STROKE * 2}px`}
                background="neutral.000"
                borderRadius="50%"
              />
            </Skeleton>
          ) : (
            <PieChart width={DIAMETER} height={DIAMETER}>
              <Pie
                data={chartData.length ? chartData : [{ value: 1 }]} // Dummy value to render a full empty circle
                dataKey="value"
                innerRadius={DIAMETER / 2 - STROKE}
                outerRadius={DIAMETER / 2}
                startAngle={90} // Start at the 12:00 position
                endAngle={-270} // End at the 12:00 position
                fill={colors.neutral[200]}
                stroke="none"
                isAnimationActive
              >
                {chartData.map((cell, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={cell.color}
                    strokeWidth={0}
                    style={{ outline: 'none' }}
                  />
                ))}
              </Pie>

              <foreignObject x="0" y="0" width={DIAMETER} height={DIAMETER}>
                <FlexBox
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  <AppLink
                    path="/search/conversations"
                    params={createSearchUrlParams({
                      filters: {
                        strategy: 'all',
                        filters: [
                          {
                            field: 'initial_channel',
                            operator: 'is_one',
                            values: channels.map((channel) => ({
                              value: convertChannelCasing(channel, 'truncate'),
                            })),
                          },
                          {
                            field: 'status',
                            operator: 'is_one',
                            values: [{ value: 'open' }],
                          },
                          {
                            field: 'queue_id',
                            operator: 'is_not_empty',
                            values: [],
                          },
                        ],
                      },
                    })}
                    onClick={() => {
                      ampli.dashboardChannelPendingConversationsMetricClicked({
                        metric: 'total',
                      });
                    }}
                  >
                    <Text.Paragraph
                      as="span"
                      variant="3"
                      color={total ? 'primary' : 'tertiary'}
                      fontSize={totalFontSize}
                    >
                      {formatInstrumentNumber(total, 'number')}
                    </Text.Paragraph>
                  </AppLink>
                </FlexBox>
              </foreignObject>
            </PieChart>
          )}

          <DashboardLegend items={items} isSkeleton={isSkeleton} />
        </FlexBox>
      </ChannelDashboardMetric>
    );
  },
);
